/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetSkillRequest,
  GetSkillSecurity,
} from "../models/operations/getskill.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillQuery,
  prefetchSkill,
  queryKeySkill,
  SkillQueryData,
} from "./skill.core.js";
export { buildSkillQuery, prefetchSkill, queryKeySkill, type SkillQueryData };

export type SkillQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * get skills
 *
 * @remarks
 * Get an active skill and its latest version. The implementation requires the skills product feature and skill read scope.
 */
export function useSkill(
  request: GetSkillRequest,
  security?: GetSkillSecurity | undefined,
  options?: QueryHookOptions<SkillQueryData, SkillQueryError>,
): UseQueryResult<SkillQueryData, SkillQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * get skills
 *
 * @remarks
 * Get an active skill and its latest version. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillSuspense(
  request: GetSkillRequest,
  security?: GetSkillSecurity | undefined,
  options?: SuspenseQueryHookOptions<SkillQueryData, SkillQueryError>,
): UseSuspenseQueryResult<SkillQueryData, SkillQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSkillData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillQueryData,
): SkillQueryData | undefined {
  const key = queryKeySkill(...queryKeyBase);

  return client.setQueryData<SkillQueryData>(key, data);
}

export function invalidateSkill(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "get", ...queryKeyBase],
  });
}

export function invalidateAllSkill(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "get"],
  });
}
